import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Download, FileText, RefreshCcw, CalendarDays } from 'lucide-react';

const MONTHS = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

/**
 * Página Reportes — hoy contiene el informe "Días fichados por empleado/mes".
 * En el futuro se pueden agregar más informes en pestañas.
 */
export default function ReportesPage() {
  const { apiFetch } = useAuth();
  const now = new Date();
  const [year,  setYear]  = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [centro, setCentro] = useState('');
  const [centros, setCentros] = useState([]);
  const [search, setSearch] = useState('');
  const [rows,   setRows]   = useState([]);
  const [totalEmp, setTotalEmp] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await apiFetch('/api/kpis/centros');
        if (!r.ok) return;
        const data = await r.json();
        if (!cancel) setCentros(Array.isArray(data) ? data : []);
      } catch (_) { /* ignore */ }
    })();
    return () => { cancel = true; };
  }, [apiFetch]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ year, month });
      if (centro) params.append('centro', centro);
      if (search) params.append('search', search);
      const r = await apiFetch(`/api/reports/dias-fichados?${params}`);
      const data = await r.json();
      if (!r.ok) {
        toast.error(data?.detail || `Error ${r.status}`);
        return;
      }
      setRows(data?.data || []);
      setTotalEmp(data?.total_empleados || 0);
    } catch (e) {
      toast.error('Error al cargar informe');
    } finally {
      setLoading(false);
    }
  }, [apiFetch, year, month, centro, search]);

  useEffect(() => { load(); /* on mount */ }, []);

  const handleExport = async (fmt) => {
    setExporting(true);
    try {
      const params = new URLSearchParams({ year, month, format: fmt });
      if (centro) params.append('centro', centro);
      if (search) params.append('search', search);
      const res = await apiFetch(`/api/reports/dias-fichados/export?${params}`);
      if (!res.ok) {
        toast.error(`Error ${res.status} al exportar`);
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `dias_fichados_${year}-${String(month).padStart(2, '0')}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exportado: ${a.download}`);
    } catch (e) {
      toast.error('Error al exportar');
    } finally {
      setExporting(false);
    }
  };

  const years = React.useMemo(() => {
    const arr = [];
    const y0 = now.getFullYear();
    for (let y = y0 - 3; y <= y0 + 1; y += 1) arr.push(y);
    return arr;
  }, [now]);

  return (
    <div data-testid="reportes-page" style={{ padding: '32px', overflowY: 'auto' }}>
      <div style={{ marginBottom: '20px' }}>
        <h1 style={{ fontFamily: 'Space Grotesk', fontSize: '24px', fontWeight: 700, color: '#f1f5f9', margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
          <FileText size={22} style={{ color: '#60a5fa' }} />
          Reportes
        </h1>
        <p style={{ color: '#64748b', fontSize: '14px', marginTop: '4px' }}>
          Informes agregados y descargables. Comienza con el número de días fichados por empleado en un mes concreto.
        </p>
      </div>

      {/* Filters */}
      <motion.form
        initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
        onSubmit={(e) => { e.preventDefault(); load(); }}
        style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '12px', padding: '16px', marginBottom: '16px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px', alignItems: 'end' }}
      >
        <div>
          <Label style={{ color: '#94a3b8', fontSize: 11 }}>Año</Label>
          <select
            data-testid="reportes-year"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            style={{ background: '#0f172a', border: '1px solid #334155', color: '#f1f5f9', borderRadius: 8, padding: '8px 10px', width: '100%', marginTop: 4, fontSize: 13 }}
          >
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div>
          <Label style={{ color: '#94a3b8', fontSize: 11 }}>Mes</Label>
          <select
            data-testid="reportes-month"
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
            style={{ background: '#0f172a', border: '1px solid #334155', color: '#f1f5f9', borderRadius: 8, padding: '8px 10px', width: '100%', marginTop: 4, fontSize: 13 }}
          >
            {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
        </div>
        <div>
          <Label style={{ color: '#94a3b8', fontSize: 11 }}>Centro</Label>
          <select
            data-testid="reportes-centro"
            value={centro}
            onChange={(e) => setCentro(e.target.value)}
            style={{ background: '#0f172a', border: '1px solid #334155', color: '#f1f5f9', borderRadius: 8, padding: '8px 10px', width: '100%', marginTop: 4, fontSize: 13 }}
          >
            <option value="">Todos</option>
            {centros.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <Label style={{ color: '#94a3b8', fontSize: 11 }}>Buscar (nombre / PNR)</Label>
          <Input data-testid="reportes-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="ej. Jesús Abad" style={{ background: '#0f172a', border: '1px solid #334155', color: '#f1f5f9', marginTop: 4 }} />
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button type="submit" data-testid="reportes-apply" disabled={loading} style={{ background: '#2563eb', color: 'white', height: 36, gap: 6, display: 'flex', alignItems: 'center' }}>
            <CalendarDays size={14} /> Ver
          </Button>
          <Button type="button" onClick={load} data-testid="reportes-refresh" variant="outline" disabled={loading} style={{ height: 36, background: '#0f172a', color: '#94a3b8', border: '1px solid #334155', gap: 6, display: 'flex', alignItems: 'center' }}>
            <RefreshCcw size={14} />
          </Button>
        </div>
      </motion.form>

      {/* Actions */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
        <Button onClick={() => handleExport('xlsx')} disabled={exporting || rows.length === 0} data-testid="reportes-export-xlsx" style={{ background: '#065f46', color: '#d1fae5', height: 34, gap: 6, display: 'flex', alignItems: 'center' }}>
          <Download size={14} /> Exportar XLSX
        </Button>
        <Button onClick={() => handleExport('csv')} disabled={exporting || rows.length === 0} data-testid="reportes-export-csv" variant="outline" style={{ background: '#0f172a', border: '1px solid #334155', color: '#94a3b8', height: 34, gap: 6, display: 'flex', alignItems: 'center' }}>
          <Download size={14} /> CSV
        </Button>
        <span style={{ color: '#64748b', fontSize: 12 }}>
          {totalEmp.toLocaleString('es-ES')} empleado(s) en {MONTHS[month - 1]} {year}
        </span>
      </div>

      {/* Table */}
      <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '12px', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: '#0f172a', color: '#94a3b8', textAlign: 'left', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                <th style={{ padding: '12px' }}>Nº Personal</th>
                <th style={{ padding: '12px' }}>Apellidos, Nombre</th>
                <th style={{ padding: '12px' }}>Puesto</th>
                <th style={{ padding: '12px' }}>Centro</th>
                <th style={{ padding: '12px', textAlign: 'right' }}>Días fichados</th>
                <th style={{ padding: '12px', textAlign: 'right' }}>Horas totales</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} style={{ padding: '32px', textAlign: 'center', color: '#64748b' }}>Cargando…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={6} style={{ padding: '48px', textAlign: 'center', color: '#475569' }}>
                  No hay fichajes registrados en {MONTHS[month - 1]} {year} con los filtros actuales.
                </td></tr>
              ) : rows.map((r) => (
                <tr key={r.id_employee} data-testid={`report-row-${r.id_employee}`} style={{ borderTop: '1px solid #0f172a', color: '#cbd5e1' }}>
                  <td style={{ padding: '10px 12px', fontFamily: 'IBM Plex Mono', color: '#94a3b8' }}>#{r.id_employee}</td>
                  <td style={{ padding: '10px 12px', color: '#f1f5f9' }}>{(r.apellidos || '') + (r.nombre ? `, ${r.nombre}` : '')}</td>
                  <td style={{ padding: '10px 12px', color: '#94a3b8' }}>{r.puesto || '—'}</td>
                  <td style={{ padding: '10px 12px', color: '#94a3b8' }}>{r.centro || '—'}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: 'IBM Plex Mono', color: '#4ade80', fontWeight: 700, fontSize: 14 }}>
                    {r.dias}
                  </td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: 'IBM Plex Mono', color: '#60a5fa' }}>
                    {(r.hours_total ?? 0).toFixed(2)}h
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
